// Mangle verb — chain effect blocks on a source sample.
//
// Self-contained like chop: loads its own source, applies a chain, hands
// the resulting buffer to the forge shell for trim/commit.

import { useCallback, useMemo, useRef, useState } from 'react';
import { decodeAudioFile } from '../../import';
import { fromPcm, toPcm } from '../../pipeline';
import type { MangleBlock } from '../../types';
import type { VerbProps } from '../types';
import { presetChain, runChain, type PresetId } from './mangleEffects';

type AddableKind = MangleBlock['kind'];

const ADDABLE_KINDS: { id: AddableKind; label: string }[] = [
  { id: 'reverse', label: 'Reverse' },
  { id: 'pitch', label: 'Pitch' },
  { id: 'stretch', label: 'Stretch' },
  { id: 'bitcrush', label: 'Bitcrush' },
  { id: 'ringmod', label: 'RingMod' },
  { id: 'distort', label: 'Distort' },
  { id: 'filter', label: 'Filter' },
];

const PRESETS: { id: PresetId; label: string }[] = [
  { id: 'crystallize', label: 'Crystallize' },
  { id: 'tape', label: 'Tape' },
  { id: 'underwater', label: 'Underwater' },
  { id: 'bitreduce', label: 'Bit-reduce' },
  { id: 'octave-down', label: 'Octave down' },
];

function defaultBlock(kind: AddableKind): MangleBlock {
  switch (kind) {
    case 'reverse':
      return { kind: 'reverse' };
    case 'pitch':
      return { kind: 'pitch', semitones: 0 };
    case 'stretch':
      return { kind: 'stretch', factor: 1, grainMs: 80, overlap: 0.5 };
    case 'bitcrush':
      return { kind: 'bitcrush', bits: 8, downsample: 2 };
    case 'ringmod':
      return { kind: 'ringmod', hz: 100, depth: 0.5 };
    case 'distort':
      return { kind: 'distort', curve: 'soft', amount: 0.3 };
    case 'filter':
      return { kind: 'filter', mode: 'lpf', hz: 1000, q: 1 };
    case 'convolve':
      return { kind: 'convolve', withSampleId: '' };
  }
}

export function MangleVerb({ audioContext, storage, onBuffer, onStatus }: VerbProps) {
  const [sourceBuffer, setSourceBuffer] = useState<AudioBuffer | null>(null);
  const [sourceName, setSourceName] = useState('');
  const [chain, setChain] = useState<MangleBlock[]>([]);
  const [rendering, setRendering] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const loadFile = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0 || !audioContext) return;
      const f = files.item(0);
      if (!f) return;
      const result = await decodeAudioFile(f, audioContext);
      if (!result.ok) {
        onStatus(`Load rejected: ${result.reason}`, 'err');
        return;
      }
      setSourceBuffer(result.buffer);
      setSourceName(result.suggestedName);
      onStatus(
        `Loaded ${result.filename}. Add blocks or pick a preset.`,
        'ok',
      );
    },
    [audioContext, onStatus],
  );

  const addBlock = (kind: AddableKind) =>
    setChain((prev) => [...prev, defaultBlock(kind)]);

  const removeBlock = (idx: number) =>
    setChain((prev) => prev.filter((_, i) => i !== idx));

  const moveBlock = (idx: number, dir: -1 | 1) =>
    setChain((prev) => {
      const next = [...prev];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return prev;
      [next[idx], next[swap]] = [next[swap]!, next[idx]!];
      return next;
    });

  const patchBlock = (idx: number, patch: Partial<MangleBlock>) =>
    setChain((prev) =>
      prev.map((b, i) => (i === idx ? ({ ...b, ...patch } as MangleBlock) : b)),
    );

  const applyPreset = (id: PresetId) => setChain(presetChain(id));

  const handleRender = useCallback(async () => {
    if (!sourceBuffer || !audioContext) return;
    if (chain.length === 0) {
      onStatus('Chain is empty — add at least one block.', 'warn');
      return;
    }
    setRendering(true);
    try {
      let pcm = toPcm(sourceBuffer);
      pcm = await runChain(pcm, chain, { ctx: audioContext, storage });
      const outBuffer = fromPcm(audioContext, pcm);
      onBuffer(outBuffer, {
        suggestedName: `${sourceName}_mangled`,
        origin: {
          kind: 'mangle',
          parentId: 'mangle-' + Date.now(),
          chain: [...chain],
        },
      });
      onStatus('Rendered — trim and save.', 'ok');
    } catch (e) {
      onStatus(
        'Render failed: ' + (e instanceof Error ? e.message : String(e)),
        'err',
      );
    } finally {
      setRendering(false);
    }
  }, [sourceBuffer, audioContext, storage, chain, sourceName, onBuffer, onStatus]);

  if (!sourceBuffer) {
    return (
      <div className="flex flex-col gap-4 min-h-[200px]">
        <div className="bg-zinc-900 border border-zinc-800 px-4 py-3 text-xs text-zinc-400">
          Load any sample and transform it through a chain of effect blocks.
        </div>
        <div className="flex items-center justify-center border-2 border-dashed border-zinc-700 rounded-lg p-10 min-h-[150px]">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-yellow-300 text-zinc-950 font-semibold"
          >
            Choose source file
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*"
            style={{ display: 'none' }}
            onChange={(e) => void loadFile(e.target.files)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 min-h-[200px]">
      <div className="flex items-center justify-between bg-zinc-900 border border-zinc-800 px-4 py-2 text-xs text-zinc-400">
        <span>
          {sourceName} · {sourceBuffer.duration.toFixed(2)}s · {chain.length} block
          {chain.length === 1 ? '' : 's'}
        </span>
        <button
          type="button"
          onClick={() => {
            setSourceBuffer(null);
            setChain([]);
          }}
          className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-200"
        >
          ← change source
        </button>
      </div>

      <section>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Presets</div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => applyPreset(p.id)}
              className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800"
            >
              {p.label}
            </button>
          ))}
        </div>
      </section>

      <section>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Add block</div>
        <div className="flex flex-wrap gap-2">
          {ADDABLE_KINDS.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => addBlock(b.id)}
              className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800"
            >
              + {b.label}
            </button>
          ))}
        </div>
      </section>

      <section>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">Chain</div>
        {chain.length === 0 ? (
          <div className="bg-zinc-900 border border-zinc-800 px-4 py-3 text-xs text-zinc-500">
            Chain is empty. Pick a preset or add a block above.
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {chain.map((block, i) => (
              <BlockRow
                key={i}
                index={i}
                last={i === chain.length - 1}
                block={block}
                onPatch={(p) => patchBlock(i, p)}
                onRemove={() => removeBlock(i)}
                onMoveUp={() => moveBlock(i, -1)}
                onMoveDown={() => moveBlock(i, 1)}
              />
            ))}
          </div>
        )}
      </section>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void handleRender()}
          disabled={chain.length === 0 || rendering}
          className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-yellow-300 text-zinc-950 font-semibold disabled:opacity-30"
        >
          {rendering ? 'Rendering…' : '● Render'}
        </button>
      </div>
    </div>
  );
}

function BlockRow({
  index,
  last,
  block,
  onPatch,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  index: number;
  last: boolean;
  block: MangleBlock;
  onPatch: (p: Partial<MangleBlock>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  return (
    <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 px-3 py-2 text-xs">
      <span className="tabular-nums text-zinc-500 w-5 text-right">{index + 1}.</span>
      <span className="uppercase tracking-[0.2em] text-[10px] text-yellow-300 w-20">
        {block.kind}
      </span>
      <div className="flex-1 flex items-center gap-3 flex-wrap">
        <BlockParams block={block} onPatch={onPatch} />
      </div>
      <button
        type="button"
        onClick={onMoveUp}
        disabled={index === 0}
        className="px-1.5 text-zinc-400 hover:text-zinc-200 disabled:opacity-30"
        aria-label="Move up"
      >
        ↑
      </button>
      <button
        type="button"
        onClick={onMoveDown}
        disabled={last}
        className="px-1.5 text-zinc-400 hover:text-zinc-200 disabled:opacity-30"
        aria-label="Move down"
      >
        ↓
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="px-1.5 text-zinc-500 hover:text-rose-400"
        aria-label="Remove block"
      >
        ✕
      </button>
    </div>
  );
}

function BlockParams({
  block,
  onPatch,
}: {
  block: MangleBlock;
  onPatch: (p: Partial<MangleBlock>) => void;
}) {
  switch (block.kind) {
    case 'reverse':
      return <span className="text-zinc-500 italic">(no params)</span>;
    case 'pitch':
      return (
        <InlineSlider
          label="semitones"
          value={block.semitones}
          min={-24}
          max={24}
          step={1}
          display={block.semitones > 0 ? `+${block.semitones}` : `${block.semitones}`}
          onChange={(v) => onPatch({ semitones: v } as Partial<MangleBlock>)}
        />
      );
    case 'stretch':
      return (
        <>
          <InlineSlider
            label="factor"
            value={block.factor}
            min={0.25}
            max={4}
            step={0.05}
            display={`${block.factor.toFixed(2)}×`}
            onChange={(v) => onPatch({ factor: v } as Partial<MangleBlock>)}
          />
          <InlineSlider
            label="grain"
            value={block.grainMs}
            min={20}
            max={500}
            step={5}
            display={`${block.grainMs}ms`}
            onChange={(v) => onPatch({ grainMs: v } as Partial<MangleBlock>)}
          />
        </>
      );
    case 'bitcrush':
      return (
        <>
          <InlineSlider
            label="bits"
            value={block.bits}
            min={1}
            max={16}
            step={1}
            display={`${block.bits}b`}
            onChange={(v) => onPatch({ bits: v } as Partial<MangleBlock>)}
          />
          <InlineSlider
            label="downsample"
            value={block.downsample}
            min={1}
            max={32}
            step={1}
            display={`÷${block.downsample}`}
            onChange={(v) => onPatch({ downsample: v } as Partial<MangleBlock>)}
          />
        </>
      );
    case 'ringmod':
      return (
        <>
          <InlineSlider
            label="hz"
            value={block.hz}
            min={1}
            max={2000}
            step={1}
            display={`${block.hz}Hz`}
            onChange={(v) => onPatch({ hz: v } as Partial<MangleBlock>)}
          />
          <InlineSlider
            label="depth"
            value={block.depth}
            min={0}
            max={1}
            step={0.01}
            display={block.depth.toFixed(2)}
            onChange={(v) => onPatch({ depth: v } as Partial<MangleBlock>)}
          />
        </>
      );
    case 'distort':
      return (
        <InlineSlider
          label="amount"
          value={block.amount}
          min={0}
          max={1}
          step={0.01}
          display={block.amount.toFixed(2)}
          onChange={(v) => onPatch({ amount: v } as Partial<MangleBlock>)}
        />
      );
    case 'filter':
      return (
        <>
          <select
            value={block.mode}
            onChange={(e) => onPatch({ mode: e.target.value as 'lpf' | 'hpf' | 'bpf' } as Partial<MangleBlock>)}
            className="bg-zinc-950 border border-zinc-700 px-1 py-0.5 text-zinc-200 text-[10px] uppercase"
          >
            <option value="lpf">LPF</option>
            <option value="hpf">HPF</option>
            <option value="bpf">BPF</option>
          </select>
          <InlineSlider
            label="hz"
            value={block.hz}
            min={40}
            max={10000}
            step={10}
            display={`${block.hz}Hz`}
            onChange={(v) => onPatch({ hz: v } as Partial<MangleBlock>)}
          />
          <InlineSlider
            label="q"
            value={block.q}
            min={0.1}
            max={10}
            step={0.1}
            display={block.q.toFixed(1)}
            onChange={(v) => onPatch({ q: v } as Partial<MangleBlock>)}
          />
        </>
      );
    case 'convolve':
      return (
        <span className="text-zinc-500 italic text-[10px]">
          (requires a local sample — wire-in in D4 provenance view)
        </span>
      );
  }
}

function InlineSlider({
  label,
  value,
  min,
  max,
  step,
  display,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  display: string;
  onChange: (n: number) => void;
}) {
  return (
    <label className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
      {label}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="accent-yellow-300 w-20"
      />
      <span className="text-zinc-300 normal-case tabular-nums w-16 text-right">
        {display}
      </span>
    </label>
  );
}
